import { AccordionV2 } from "./AccordionV2.js";
import ViewResource from "./ViewResource.js";
import {
  useViewResourcesSuspense,
  type ViewResourcesResponse,
  type ViewResourcesRequest
} from "@swivu/client-api";
import { groupBy } from "../utils/groupBy.js";
import { Badge } from "./Badge.js";
import { createContext, useContext } from "react";
import SuspenseWithDelay from "./SuspenseWithDelay.js";
import ErrorBoundary from "./ErrorBoundary.js";
import { useIsGummy } from "../hooks/useGummyTheme.js";
import { cn } from "../utils/cn.js";
import { tilesForIds } from "../utils/tilePalette.js";

type ResourceResult = ViewResourcesResponse[number];

type ResourceResultsWithoutGroupValueNull = ResourceResult & {
  _group_value: string;
};

type ViewResourcesVariables = ViewResourcesRequest & {
  id: string;
  baseDetailUrl: string;
};
type ViewResourcesProps = ViewResourcesVariables & {
  resources: ViewResourcesResponse;
};

const ViewResourcesContext = createContext<ViewResourcesProps | null>(null);

const ViewResourcesProviderWrapper = ({
  children,
  ...props
}: ViewResourcesVariables & { children: React.ReactNode }) => {
  return (
    <SuspenseWithDelay fallback={<ViewResourcesSkeleton />}>
      <ErrorBoundary>
        <ViewResourcesProvider {...props}>{children}</ViewResourcesProvider>
      </ErrorBoundary>
    </SuspenseWithDelay>
  );
};

const ViewResourcesSkeleton = () => {
  const isGummy = useIsGummy();
  return (
    <div className="h-full w-full animate-pulse space-y-3">
      <div
        className={cn(
          "bg-mono-200 dark:bg-mono-900 h-12 w-full",
          isGummy ? "rounded-surface-md" : "rounded-md"
        )}
      />
      {isGummy && (
        <div className="space-y-3">
          <div className="rounded-surface-lg bg-mono-200 dark:bg-mono-900 h-28" />
          <div className="rounded-surface-lg bg-mono-200 dark:bg-mono-900 h-28" />
        </div>
      )}
    </div>
  );
};

const ViewResourcesProvider = ({
  children,
  ...props
}: ViewResourcesVariables & { children: React.ReactNode }) => {
  const { data } = useViewResourcesSuspense({
    variables: {
      ...props
    }
  });
  return (
    <ViewResourcesContext.Provider value={{ ...props, resources: data }}>
      {children}
    </ViewResourcesContext.Provider>
  );
};

const useViewResources = () => {
  const context = useContext(ViewResourcesContext);
  if (!context) {
    throw new Error(
      "useViewResources must be used within a ViewResourcesProvider"
    );
  }
  return context;
};

const ViewResources = () => {
  const { id, resources, baseDetailUrl } = useViewResources();
  const isGummy = useIsGummy();

  const resourcesWithoutNull = resources.map((resource) => {
    if (resource._group_value === null) {
      resource._group_value = `Not assigned to a task`;
    }
    return resource;
  }) as ResourceResultsWithoutGroupValueNull[];
  const groupedResources = groupBy(resourcesWithoutNull, "_group_value");
  return (
    <div className="mx-auto">
      <AccordionV2.PersistentRoot
        persistKey={`view-resources-${id}`}
        defaultValue={["undefined"]}
      >
        {Object.entries(groupedResources).map(([key, resources]) => {
          const tileColors = tilesForIds(resources.map((r) => r.id));
          return (
            <AccordionV2.Item key={key} value={key ?? "Resources"}>
              <div className="bg-mono-200 dark:bg-mono-950 sticky top-[0px] z-10 -mx-2 px-2">
                <AccordionV2.Trigger
                  className="mobile-pref-view-trigger inline-flex w-full py-0"
                  headerClassName=" flex space-x-4 !border-b-0 py-0"
                >
                  <h3
                    className={cn(
                      "mobile-pref-section-title dark:border-mono-700/50 flex items-center justify-between gap-4 py-3",
                      isGummy
                        ? "font-display text-2xl font-black tracking-tight text-mono-900 dark:text-white"
                        : "text-lg"
                    )}
                  >
                    <div className={cn(isGummy ? "font-black" : "font-bold")}>
                      <span>{key === "undefined" ? "Resources" : key}</span>
                    </div>
                    <Badge
                      count={resources.length}
                      action="info"
                      className={cn(
                        "mobile-pref-detail-badge static shrink-0",
                        isGummy && "h-10 min-w-10 px-3 text-base font-black"
                      )}
                    />
                  </h3>
                </AccordionV2.Trigger>
              </div>
              <AccordionV2.Content>
                <ul
                  className={cn(
                    "grid grid-cols-1 gap-3 sm:grid-cols-1",
                    !isGummy &&
                      "divide-mono-300 dark:divide-mono-700/50 gap-4 divide-y"
                  )}
                >
                  {resources.map((resource, i) => {
                    const urlSearchParams = new URLSearchParams();
                    urlSearchParams.set("ids", resource.id.toString());
                    urlSearchParams.set(
                      "titles",
                      resource.label || resource.display_name || ""
                    );
                    return (
                      <div
                        className={cn(
                          isGummy
                            ? cn(
                                "relative overflow-hidden px-5 text-white text-shadow-on-color",
                                "rounded-surface-lg",
                                tileColors[i],
                                "shadow-card"
                              )
                            : "dark:bg-mono-900 bg-mono-50 rounded-md px-4"
                        )}
                        key={resource.id}
                      >
                        {isGummy && <span aria-hidden className="top-gloss" />}
                        <ViewResource
                          resource={resource}
                          baseDetailUrl={baseDetailUrl}
                        />
                      </div>
                    );
                  })}
                </ul>
              </AccordionV2.Content>
            </AccordionV2.Item>
          );
        })}
      </AccordionV2.PersistentRoot>
    </div>
  );
};

ViewResources.Provider = ViewResourcesProviderWrapper;
ViewResources.useViewResources = useViewResources;
ViewResources.displayName = "ViewResources";
export default ViewResources;
